<template>
  <main class="invite-register-success">
    <section class="success-content">
      <img class="success-background" :src="backgroundImage" alt="" />

      <div class="success-copy">
        <h1>
          <span>{{ t("auth.inviteRegisterSuccessTitlePrefix") }}</span>
          <span class="title-highlight">
            {{ t("auth.inviteRegisterSuccessTitleHighlight") }}
          </span>
        </h1>
        <p>
          {{ t("auth.inviteRegisterSuccessDescLine1") }}<br />
          {{ t("auth.inviteRegisterSuccessDescLine2") }}
        </p>

        <div class="download-options" aria-label="App download options">
          <a
            class="download-option download-option-android"
            href="https://winpay.com/download.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img :src="androidIcon" alt="" />
            <span>Android APK</span>
          </a>
          <a
            class="download-option download-option-apple"
            href="https://apps.apple.com/us/app/winpay/id6746402622"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img :src="appleIcon" alt="" />
            <span>App Store</span>
          </a>
        </div>

        <div class="feature-list">
          <div class="feature-item">
            <img :src="checkIcon" alt="" />
            <span>{{ t("auth.inviteRegisterSuccessFeatureGlobal") }}</span>
          </div>
          <img class="feature-divider" :src="dividerIcon" alt="" />
          <div class="feature-item">
            <img :src="giftIcon" alt="" />
            <span>{{ t("auth.inviteRegisterSuccessFeatureServices") }}</span>
          </div>
          <img class="feature-divider" :src="dividerIcon" alt="" />
          <div class="feature-item">
            <img :src="protectIcon" alt="" />
            <span>{{ t("auth.inviteRegisterSuccessFeatureSecure") }}</span>
          </div>
        </div>
      </div>

      <button class="start-button" type="button" @click="goHome">
        <span>{{ t("auth.inviteRegisterSuccessAction") }}</span>
        <img :src="arrowIcon" alt="" />
      </button>
    </section>
  </main>
</template>

<script setup>
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import backgroundImage from "@/assets/images/loginImage/register_success_bg.png";
import androidIcon from "@/assets/images/SVG/icon/auth-register-android.svg";
import appleIcon from "@/assets/images/SVG/icon/auth-register-apple.svg";
import arrowIcon from "@/assets/images/SVG/icon/auth-register-arrow-right.svg";
import checkIcon from "@/assets/images/SVG/icon/auth-register-check.svg";
import dividerIcon from "@/assets/images/SVG/icon/auth-register-divider.svg";
import giftIcon from "@/assets/images/SVG/icon/auth-register-gift.svg";
import protectIcon from "@/assets/images/SVG/icon/auth-register-protect.svg";

const { t } = useI18n();
const router = useRouter();

function goHome() {
  router.replace("/home/index");
}
</script>

<style scoped lang="scss">
.invite-register-success {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: var(--app-viewport-height);
  min-height: 680px;
  overflow: hidden;
  background: #f8faff;
}

.success-header {
  position: relative;
  z-index: 1;
  flex: 0 0 88px;
  height: 88px;
  padding-top: 14px;
  color: #111827;
}

.status-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 16px;
  padding: 0 23px;
  font-size: 14px;
  font-weight: 600;
  line-height: 16px;
}

.status-icons {
  display: flex;
  align-items: center;
  gap: 4px;

  img {
    display: block;
    flex: none;
    object-fit: contain;
  }
}

.brand-title {
  margin-top: 22px;
  font-size: 18px;
  font-weight: 600;
  line-height: 22px;
  text-align: center;
}

.success-content {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  background: #dce5fa;
}

.success-background {
  position: absolute;
  top: -62px;
  left: 0;
  display: block;
  width: 100%;
  height: calc(100% + 62px);
  object-fit: cover;
  object-position: center top;
  opacity: 0.8;
}

.success-copy {
  position: relative;
  z-index: 1;
  width: min(335px, calc(100% - 32px));
  margin: 0 auto;
  padding-top: 70px;
  color: #111c4a;
  text-align: center;
}

h1 {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin: 0;
  font-size: clamp(25px, 8.6vw, 34px);
  font-weight: 700;
  line-height: 1.08;
}

.title-highlight {
  color: #2667ff;
}

.success-copy p {
  margin: 14px 0 0;
  color: #283761;
  font-size: 14px;
  font-weight: 400;
  line-height: 19px;
}

.download-options {
  display: flex;
  justify-content: center;
  gap: 21px;
  margin-top: 14px;
}

.download-option {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 147px;
  height: 36px;
  border-radius: 20px;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
  white-space: nowrap;

  img {
    display: block;
    width: auto;
    height: auto;
    flex: none;
    object-fit: contain;
  }
}

.download-option-android {
  background: linear-gradient(180deg, #1a2136 0%, #2a385b 105.56%);
  border-bottom: 3px solid #161f37;
}

.download-option-apple {
  background: linear-gradient(160deg, #2667ff 7.58%, #44b2fd 102.58%);
  border-bottom: 3px solid #2f7dfe;
}

.feature-list {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 20px;
  color: #4e6197;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  line-height: 16px;
  white-space: nowrap;

  img {
    display: block;
    width: 16px;
    height: 16px;
    flex: none;
    object-fit: contain;
  }
}

.feature-divider {
  display: block;
  width: 1px;
  height: 12.5px;
  flex: none;
  object-fit: contain;
}

.start-button {
  position: absolute;
  z-index: 1;
  bottom: 10%;
  left: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: min(243px, calc(100% - 60px));
  height: 47px;
  padding: 0 44px 0 52px;
  transform: translateX(-50%);
  border: 0;
  border-radius: 24px;
  color: #fff;
  border-radius: 50px;
  background: linear-gradient(90deg, #0c4bf0 0%, #46a8fe 100%);
  box-shadow: 0 3px 5.4px 0 rgba(255, 255, 255, 0.6) inset;
  border-top: 0px solid #46a8fe;
  border-left: 0px solid #46a8fe;
  border-right: 0px solid #46a8fe;
  border-bottom: 3px solid #0752eb;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  white-space: nowrap;

  img {
    position: absolute;
    right: 16px;
    display: block;
    width: 24px;
    height: 24px;
    object-fit: contain;
  }
}

@media (max-width: 360px) {
  .feature-list {
    gap: 6px;
  }

  .feature-item {
    gap: 3px;
    font-size: 11px;
  }
}
</style>
